import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { useFetch } from "@/lib/axios.config";
import { PaginationResponse } from "@/shared/types/api.types";
import {
  SurveyListItem,
  SurveyDetail,
  SurveyAnswerPayload,
  SurveySubmitResult,
  SaveAnswerResult,
} from "./surveys.types";

export enum SurveyKeys {
  SurveyList = "surveyList",
  SurveyDetail = "surveyDetail",
}

export const useSurveyListOptions = (opts?: {
  applicationId?: string | null;
  // true bo'lsa backend faqat admin "show_in_home" belgilagan survey'larni
  // qaytaradi (home check-in slider uchun). Berilmasa — barcha active.
  showInHome?: boolean;
}) => {
  const { applicationId, showInHome } = opts ?? {};
  return queryOptions({
    queryKey: [SurveyKeys.SurveyList, applicationId, showInHome],
    queryFn: async () => {
      const res = await useFetch.get<PaginationResponse<SurveyListItem>>(
        "/survey/SurveyList",
        {
          params: {
            limit: 100,
            application_id: applicationId || undefined,
            show_in_home: showInHome ? true : undefined,
          },
        }
      );
      return res.data.results;
    },
  });
};

export const useSurveyDetailOptions = (surveyId?: string) => {
  return queryOptions({
    queryKey: [SurveyKeys.SurveyDetail, surveyId],
    queryFn: async () => {
      const res = await useFetch.get<SurveyDetail>(
        `/survey/SurveyDetail/${surveyId}`
      );
      return res.data;
    },
    enabled: !!surveyId,
  });
};

export const useSaveAnswerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      surveyId,
      answer,
    }: {
      surveyId: string;
      answer: SurveyAnswerPayload;
    }) => {
      const res = await useFetch.post<SaveAnswerResult>(
        `/survey/SaveAnswer/${surveyId}`,
        answer
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SurveyKeys.SurveyList] });
    },
  });
};

export const useSubmitSurveyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (surveyId: string) => {
      const res = await useFetch.post<SurveySubmitResult>(
        `/survey/SubmitSurvey/${surveyId}`
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SurveyKeys.SurveyList] });
      queryClient.invalidateQueries({ queryKey: [SurveyKeys.SurveyDetail] });
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });
};
